import { Entity, EntityMemento } from '@silvertours/lib-sys-domain';

type RentalOfferId = string;

type RentalOfferAttributes = {
  seats: number;
  doors: string;
  airConditioning: boolean;
  transmission: string;
};

type RentalOfferMemento = EntityMemento<
  RentalOfferId,
  {
    offerId: string;
    category: string;
    city: string;
    cityCode: string;
    departureDate: string;
    destinationDate: string;
    price: number;
    supplier: string;
    storedAt: string;
    carName: string;
    originalImage: string;
    attributes: RentalOfferAttributes | null;
  }
>;

const categoryFilterIds: Record<string, string> = {
  compact: 'car_class_compact',
  convertible: 'car_type_convertible',
  intermediate_standard: 'car_class_middle',
  standard_elite_full_size: 'car_class_upper',
  mini_economy: 'car_class_small',
  premium_luxus: 'car_class_premium',
  suv: 'car_type_suv_crossover',
  van_monospace: 'car_type_van_monospace',
  truck: 'car_class_transporter',
  wagon_estate: 'car_type_wagon_estate',
};

class RentalOffer extends Entity<RentalOfferId, RentalOfferMemento> {
  constructor(memento: RentalOfferMemento) {
    super('RentalOffer', memento);
  }

  static Bind(memento: RentalOfferMemento) {
    return new RentalOffer(memento);
  }

  static generateSearchQuery(category: string) {
    const filterId = categoryFilterIds[category];
    if (!filterId) {
      return '';
    }
    return new URLSearchParams({ filter: filterId }).toString();
  }

  get category() {
    return this.memento.category;
  }

  get city() {
    return this.memento.city;
  }

  get cityCode() {
    return this.memento.cityCode;
  }

  get departureDate() {
    return this.memento.departureDate;
  }

  get destinationDate() {
    return this.memento.destinationDate;
  }

  get image() {
    return `/static/default/global/graphic/car/car_classes/${this.memento.category}.png`;
  }

  get originalImage() {
    return this.memento.originalImage;
  }

  get carName() {
    return this.memento.carName;
  }

  get attributes() {
    return this.memento.attributes;
  }

  get price() {
    return this.memento.price;
  }

  get storedAt() {
    return this.memento.storedAt;
  }

  get supplier() {
    return this.memento.supplier;
  }
}

export { RentalOffer };
export type { RentalOfferAttributes, RentalOfferId, RentalOfferMemento };
